import React, { useState, useEffect, useMemo } from 'react';
import documentoPessoalService from '../services/documentoPessoalService';
import dirigenteService from '../services/dirigenteService';
import Toast from './Toast';
import FileViewerModal from './FileViewerModal';
import type { DocumentoPessoalDTO } from '../types/documentoPessoal.types';
import { useAuth } from '../context/AuthContext';
import { toUpperText } from '../utils/formatters';

interface DocumentosPessoaisUploadProps {
  dirigenteId: string;
  cargo: string;
  cargos?: string[];
  onDocumentosChange?: () => void;
  embedded?: boolean;
}

interface TipoDocumento {
  tipo: string;
  nome: string;
  obrigatorio: boolean;
  numeroObrigatorio?: boolean;
  dataEmissaoObrigatoria?: boolean;
  dataValidadeObrigatoria?: boolean;
}

const DocumentosPessoaisUpload: React.FC<DocumentosPessoaisUploadProps> = ({
  dirigenteId,
  cargo,
  cargos,
  onDocumentosChange,
  embedded = false,
}) => {
  const { user } = useAuth();
  const [documentos, setDocumentos] = useState<DocumentoPessoalDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedTipo, setSelectedTipo] = useState<TipoDocumento | null>(null);
  const [uploadData, setUploadData] = useState({
    file: null as File | null,
    numeroDocumento: '',
    dataEmissao: '',
    dataValidade: '',
    descricao: '',
  });

  // Modal para aprovar
  const [showAprovarModal, setShowAprovarModal] = useState(false);
  const [documentoParaAprovar, setDocumentoParaAprovar] = useState<DocumentoPessoalDTO | null>(null);
  const [motivoAprovacao, setMotivoAprovacao] = useState('');

  // Modal para reprovar
  const [showReprovarModal, setShowReprovarModal] = useState(false);
  const [documentoParaReprovar, setDocumentoParaReprovar] = useState<DocumentoPessoalDTO | null>(null);
  const [motivoReprovacao, setMotivoReprovacao] = useState('');
  const [documentoEmVisualizacao, setDocumentoEmVisualizacao] = useState<DocumentoPessoalDTO | null>(null);
  const [toast, setToast] = useState<{message: string; type?: 'success'|'error'|'info'} | null>(null);

  const showToast = (m: string, t: 'success'|'error'|'info' = 'info') => setToast({ message: m, type: t });

  // Check if user is OPERADOR (operators cannot approve/reject documents)
  const isOperador = useMemo(() => {
    if (!user || !user.role) return false;
    const roleString = typeof user.role === 'string' ? user.role : String(user.role);
    return roleString === 'OPERADOR';
  }, [user]);

  // Definir documentos obrigatórios baseado no cargo
  const cargosAtivos = useMemo(() => {
    const lista = Array.isArray(cargos) && cargos.length > 0 ? cargos : [cargo];
    return Array.from(new Set(lista.filter(Boolean)));
  }, [cargo, cargos]);

  const tiposDocumento: TipoDocumento[] = Array.from(new Map(cargosAtivos.flatMap((cargoAtual) => (
    cargoAtual?.toLowerCase().includes('presidente')
      ? [
        {
          tipo: 'RG_PRESIDENTE',
          nome: 'RG',
          obrigatorio: true,
          numeroObrigatorio: true,
          dataEmissaoObrigatoria: true,
        },
        {
          tipo: 'CNH_PRESIDENTE',
          nome: 'CNH',
          obrigatorio: true,
          numeroObrigatorio: true,
          dataEmissaoObrigatoria: true,
          dataValidadeObrigatoria: true,
        },
        {
          tipo: 'DOCUMENTO_FOTO_PRESIDENTE',
          nome: 'Documento com Foto',
          obrigatorio: true,
        },
        {
          tipo: 'COMPROVANTE_ENDERECO_PRESIDENTE',
          nome: 'Comprovante de Endereço',
          obrigatorio: true,
          dataEmissaoObrigatoria: true,
        },
        {
          tipo: 'CERTIDAO_TCE_PRESIDENTE',
          nome: 'Certidão Negativa TCE',
          obrigatorio: true,
          dataEmissaoObrigatoria: true,
          dataValidadeObrigatoria: true,
        },
      ]
      : [
        {
          tipo: 'DOCUMENTO_FOTO_DIRIGENTE',
          nome: 'Documento de Identificação com Foto',
          obrigatorio: true,
          numeroObrigatorio: true,
          dataEmissaoObrigatoria: true,
        },
        {
          tipo: 'CERTIDAO_TCE_DIRIGENTE',
          nome: 'Certidão Negativa TCE',
          obrigatorio: true,
          dataEmissaoObrigatoria: true,
          dataValidadeObrigatoria: true,
        },
      ]
  )).map((tipo) => [tipo.tipo, tipo] as const)).values());

  useEffect(() => {
    loadDocumentos();
  }, [dirigenteId]);

  const loadDocumentos = async () => {
    try {
      setLoading(true);
      const data = await documentoPessoalService.listarPorDirigente(dirigenteId);
      setDocumentos(data);
    } catch (error) {
      console.error('Erro ao carregar documentos:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenUpload = (tipo: TipoDocumento) => {
    const docExistente = getDocumento(tipo.tipo);
    setSelectedTipo(tipo);
    setUploadData({
      file: null,
      numeroDocumento: docExistente?.numeroDocumento || '',
      dataEmissao: '',
      dataValidade: '',
      descricao: '',
    });
    setShowUploadModal(true);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      if (file.size > 10 * 1024 * 1024) {
        showToast(`Arquivo muito grande (${(file.size / 1024 / 1024).toFixed(1)}MB). O tamanho máximo permitido é 10MB.`, 'error');
        e.target.value = '';
        return;
      }
      setUploadData({ ...uploadData, file });
    }
  };

  const handleUpload = async () => {
    if (!uploadData.file || !selectedTipo) {
      showToast('Selecione um arquivo', 'error');
      return;
    }

    // Validações
    if (selectedTipo.numeroObrigatorio && !uploadData.numeroDocumento) {
      showToast('Número do documento é obrigatório', 'error');
      return;
    }
    if (selectedTipo.dataEmissaoObrigatoria && !uploadData.dataEmissao) {
      showToast('Data de emissão é obrigatória', 'error');
      return;
    }
    if (selectedTipo.dataValidadeObrigatoria && !uploadData.dataValidade) {
      showToast('Data de validade é obrigatória', 'error');
      return;
    }

    const docExistente = getDocumento(selectedTipo.tipo);

    try {
      setUploading(true);
      if (docExistente) {
        await documentoPessoalService.substituir(docExistente.id, {
          file: uploadData.file,
          numeroDocumento: uploadData.numeroDocumento || undefined,
          dataEmissao: uploadData.dataEmissao || undefined,
          dataValidade: uploadData.dataValidade || undefined,
          descricao: uploadData.descricao || undefined,
        });
      } else {
        await documentoPessoalService.upload({
          dirigenteId,
          tipoDocumento: selectedTipo.tipo,
          file: uploadData.file,
          numeroDocumento: uploadData.numeroDocumento || undefined,
          dataEmissao: uploadData.dataEmissao || undefined,
          dataValidade: uploadData.dataValidade || undefined,
          descricao: uploadData.descricao || undefined,
        });
      }

      showToast(docExistente ? 'Documento atualizado com sucesso!' : 'Documento enviado com sucesso!', 'success');
      setShowUploadModal(false);
      await loadDocumentos();
      if (onDocumentosChange) onDocumentosChange();
    } catch (error: any) {
      console.error('Erro ao fazer upload:', error);
      showToast(error.response?.data?.error || 'Erro ao enviar documento', 'error');
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (doc: DocumentoPessoalDTO) => {
    try {
      const blob = await documentoPessoalService.download(doc.id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.nomeOriginal;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Erro ao baixar documento:', error);
      showToast('Erro ao baixar documento', 'error');
    }
  };

  const handleView = (doc: DocumentoPessoalDTO) => {
    setDocumentoEmVisualizacao(doc);
  };

  const handleOpenAprovar = (doc: DocumentoPessoalDTO) => {
    setDocumentoParaAprovar(doc);
    setMotivoAprovacao('');
    setShowAprovarModal(true);
  };

  const handleAprovar = async () => {
    if (!documentoParaAprovar) return;

    // Require motivo for approval as well
    if (!motivoAprovacao.trim()) {
      showToast('O motivo da aprovação é obrigatório', 'error');
      return;
    }

    try {
      await documentoPessoalService.aprovar(documentoParaAprovar.id, motivoAprovacao);
      showToast('Documento aprovado com sucesso!', 'success');
      setShowAprovarModal(false);
      setDocumentoParaAprovar(null);
      setMotivoAprovacao('');
      await loadDocumentos();
      if (onDocumentosChange) onDocumentosChange();
    } catch (error: any) {
      console.error('Erro ao aprovar documento:', error);
      showToast(error.response?.data?.error || 'Erro ao aprovar documento', 'error');
    }
  };

  const handleOpenReprovar = (doc: DocumentoPessoalDTO) => {
    setDocumentoParaReprovar(doc);
    setMotivoReprovacao('');
    setShowReprovarModal(true);
  };

  const handleReprovar = async () => {
    if (!documentoParaReprovar) return;

    if (!motivoReprovacao.trim()) {
      showToast('O motivo da reprovação é obrigatório', 'error');
      return;
    }

    try {
      const updated = await documentoPessoalService.reprovar(documentoParaReprovar.id, motivoReprovacao);
      showToast('Documento reprovado', 'success');
      setShowReprovarModal(false);
      setDocumentoParaReprovar(null);
      setMotivoReprovacao('');
      await loadDocumentos();
      if (onDocumentosChange) onDocumentosChange();
      // Best-effort: when a personal document is reproved, move the dirigente to the end of the Convênios queue
      try {
        if (dirigenteId) {
          dirigenteService.moveToEndQueue(dirigenteId).catch((err) => {
            console.warn('[DocumentosPessoaisUpload] moveToEndQueue failed for dirigente', dirigenteId, err);
          });
        }
      } catch (err) {
        console.warn('[DocumentosPessoaisUpload] error calling moveToEndQueue', err);
      }
    } catch (error: any) {
      console.error('Erro ao reprovar documento:', error);
      showToast(error.response?.data?.error || 'Erro ao reprovar documento', 'error');
    }
  };

  const getDocumento = (tipo: string): DocumentoPessoalDTO | undefined => {
    return documentos.find((d) => d.tipoDocumento === tipo);
  };

  const calcularDiasParaVencimento = (dataValidade: string): number => {
    // Compara só o dia (calendário local): documento que vence hoje ainda é válido (0 dias).
    const [ano, mes, dia] = dataValidade.slice(0, 10).split('-').map(Number);
    const agora = new Date();
    const hoje = Date.UTC(agora.getFullYear(), agora.getMonth(), agora.getDate());
    const vencimento = Date.UTC(ano, mes - 1, dia);
    return Math.round((vencimento - hoje) / (1000 * 60 * 60 * 24));
  };

  const isDocVencido = (doc: DocumentoPessoalDTO): boolean => {
    if (!doc.dataValidade) return false;
    return calcularDiasParaVencimento(doc.dataValidade) < 0;
  };

  const renderAlertaVencimento = (dataValidade: string) => {
    const dias = calcularDiasParaVencimento(dataValidade);
    if (dias < 0) return <p className="text-xs text-red-600 mt-1">⚠️ Vencido há {Math.abs(dias)} dia(s)</p>;
    if (dias <= 30) return <p className="text-xs text-orange-600 mt-1">⚠️ Vence em {dias} dia(s)</p>;
    return null;
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'APROVADO':
        return '✅';
      case 'REPROVADO':
        return '❌';
      case 'PENDENTE':
        return '⏳';
      default:
        return '❓';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'APROVADO':
        return 'text-green-600 bg-green-50 border-green-200';
      case 'REPROVADO':
        return 'text-red-600 bg-red-50 border-red-200';
      case 'PENDENTE':
        return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  if (loading) {
    return (
      <div className="rounded-3xl border border-slate-200 bg-white p-8 text-center text-slate-500 shadow-sm">
        Carregando documentos...
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className={embedded ? '' : 'rounded-3xl border border-slate-200 bg-gradient-to-br from-white via-slate-50 to-emerald-50 p-6 shadow-sm'}>
        {!embedded && (
        <div className="mb-4 flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-700">Documentos</p>
            <h3 className="mt-2 text-xl font-bold text-slate-900">Documentos Pessoais</h3>
            <p className="mt-1 text-sm text-slate-600">{cargo}</p>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white/90 px-4 py-3 text-right">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Obrigatórios</p>
            <p className="mt-1 text-2xl font-bold text-slate-900">{tiposDocumento.length}</p>
          </div>
        </div>
        )}

        <div className="space-y-3">
          {tiposDocumento.map((tipo) => {
            const doc = getDocumento(tipo.tipo);
            const hasDocumento = !!doc;

            const vencido = hasDocumento && isDocVencido(doc);

            return (
              <div
                key={tipo.tipo}
                className={`border p-4 ${
                  vencido
                    ? 'rounded-2xl border-red-300 bg-red-50'
                    : hasDocumento ? 'rounded-2xl border-slate-200 bg-white' : 'rounded-2xl border-orange-200 bg-orange-50'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div>
                      {hasDocumento ? (
                        <span className="text-2xl">{getStatusIcon(doc.statusDocumento)}</span>
                      ) : (
                        <span className="text-2xl">⚠️</span>
                      )}
                    </div>
                    <div>
                      <div className="font-medium">
                        {tipo.nome}
                        {tipo.obrigatorio && (
                          <span className="text-red-500 ml-1">*</span>
                        )}
                      </div>
                      {hasDocumento && (
                        <div className="text-sm text-slate-500">
                          <span className={`inline-block rounded-full px-2.5 py-1 text-xs font-medium border ${getStatusColor(doc.statusDocumento)}`}>
                            {doc.statusDocumento}
                          </span>
                          <span className="ml-2">{doc.nomeOriginal}</span>
                          {doc.dataUpload && (
                            <span className="ml-2">
                              • {new Date(doc.dataUpload).toLocaleDateString('pt-BR')}
                            </span>
                          )}
                          {doc.dataValidade && (
                            <span className="ml-2">
                              • Válido até {new Date(doc.dataValidade).toLocaleDateString('pt-BR')}
                            </span>
                          )}
                          {doc.dataValidade && renderAlertaVencimento(doc.dataValidade)}
                        </div>
                      )}
                      {!hasDocumento && (
                        <div className="text-sm text-yellow-700">
                          Documento não enviado
                        </div>
                      )}
                      {doc?.motivoReprovacao && (
                        <div className="text-sm text-red-600 mt-1">
                          <strong>Motivo da reprovação:</strong> {doc.motivoReprovacao}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    {hasDocumento && (
                      <>
                        <button
                          onClick={() => handleView(doc)}
                          className="rounded-xl border border-sky-200 bg-sky-50 px-3 py-2 text-sm font-medium text-sky-700 hover:bg-sky-100"
                        >
                          Visualizar
                        </button>
                        <button
                          onClick={() => handleDownload(doc)}
                          className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                        >
                          Download
                        </button>

                        {/* Botão Aprovar - só mostrar se não for OPERADOR e documento estiver PENDENTE */}
                        {!isOperador && doc.statusDocumento === 'PENDENTE' && (
                          <button
                            onClick={() => handleOpenAprovar(doc)}
                            className="rounded-xl bg-emerald-600 px-3 py-2 text-sm font-medium text-white hover:bg-emerald-700"
                          >
                            ✅ Aprovar
                          </button>
                        )}

                        {/* Botão Reprovar - só mostrar se não for OPERADOR e documento estiver PENDENTE */}
                        {!isOperador && doc.statusDocumento === 'PENDENTE' && (
                          <button
                            onClick={() => handleOpenReprovar(doc)}
                            className="rounded-xl bg-red-600 px-3 py-2 text-sm font-medium text-white hover:bg-red-700"
                          >
                            ❌ Reprovar
                          </button>
                        )}
                      </>
                    )}
                    <button
                      onClick={() => handleOpenUpload(tipo)}
                      className={`rounded-xl px-3 py-2 text-sm font-medium text-white ${
                        vencido ? 'bg-red-600 hover:bg-red-700' : 'bg-emerald-600 hover:bg-emerald-700'
                      }`}
                    >
                      {vencido ? '🔄 Renovar' : hasDocumento ? '🔄 Renovar' : '📤 Enviar'}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Modal de Upload */}
      {showUploadModal && selectedTipo && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="w-full max-w-md rounded-3xl border border-slate-200 bg-white shadow-xl">
            <div className="border-b border-slate-200 p-6">
              <h3 className="text-lg font-semibold text-slate-900">
              {getDocumento(selectedTipo.tipo) ? 'Renovar' : 'Enviar'} {selectedTipo.nome}
              </h3>
              {getDocumento(selectedTipo.tipo) && (
                <p className="mt-1 text-xs text-slate-500">O documento anterior será marcado como substituído.</p>
              )}
            </div>

            <div className="space-y-4 p-6">
              <div>
                <label className="block text-sm font-medium mb-1">
                  Arquivo {selectedTipo.obrigatorio && <span className="text-red-500">*</span>}
                </label>
                <input
                  type="file"
                  onChange={handleFileSelect}
                  className="w-full border rounded px-3 py-2"
                  accept=".pdf,.jpg,.jpeg,.png"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Formatos aceitos: PDF, JPG, PNG (máx. 20MB)
                </p>
              </div>

              {selectedTipo.numeroObrigatorio && (
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Número do Documento <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={uploadData.numeroDocumento}
                    onChange={(e) =>
                      setUploadData({ ...uploadData, numeroDocumento: toUpperText(e.target.value) })
                    }
                    className="w-full border rounded px-3 py-2"
                    placeholder="Ex: 12.345.678-9"
                  />
                </div>
              )}

              {selectedTipo.dataEmissaoObrigatoria && (
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Data de Emissão <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={uploadData.dataEmissao}
                    onChange={(e) =>
                      setUploadData({ ...uploadData, dataEmissao: e.target.value })
                    }
                    className="w-full border rounded px-3 py-2"
                  />
                </div>
              )}

              {selectedTipo.dataValidadeObrigatoria && (
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Data de Validade <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={uploadData.dataValidade}
                    onChange={(e) =>
                      setUploadData({ ...uploadData, dataValidade: e.target.value })
                    }
                    className="w-full border rounded px-3 py-2"
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium mb-1">
                  Observações (opcional)
                </label>
                <textarea
                  value={uploadData.descricao}
                  onChange={(e) =>
                    setUploadData({ ...uploadData, descricao: toUpperText(e.target.value) })
                  }
                  className="w-full border rounded px-3 py-2"
                  rows={3}
                  placeholder="Informações adicionais sobre o documento"
                />
              </div>
            </div>

            <div className="flex justify-end space-x-2 border-t border-slate-200 bg-slate-50 p-6">
              <button
                onClick={() => setShowUploadModal(false)}
                className="rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                disabled={uploading}
              >
                Cancelar
              </button>
              <button
                onClick={handleUpload}
                className="rounded-xl bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
                disabled={uploading || !uploadData.file}
              >
                {uploading ? 'Enviando...' : (getDocumento(selectedTipo.tipo) ? 'Renovar Documento' : 'Enviar Documento')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Aprovação */}
      {showAprovarModal && documentoParaAprovar && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="w-full max-w-md rounded-3xl border border-slate-200 bg-white shadow-xl">
            <div className="border-b border-slate-200 p-6">
              <h3 className="text-lg font-semibold text-slate-900">
              Aprovar Documento
              </h3>
            </div>

            <div className="p-6">
            <div className="mb-4">
              <p>
                Você está prestes a aprovar o documento de {documentoParaAprovar.nomeOriginal}.
              </p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">
                  Motivo <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={motivoAprovacao}
                  onChange={(e) => setMotivoAprovacao(toUpperText(e.target.value))}
                  className="w-full border rounded px-3 py-2"
                  rows={3}
                  placeholder="Motivo para aprovar este documento"
                />
              </div>
            </div>

            </div>
            <div className="flex justify-end space-x-2 border-t border-slate-200 bg-slate-50 p-6">
              <button
                onClick={() => setShowAprovarModal(false)}
                className="rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
              >
                Cancelar
              </button>
              <button
                onClick={handleAprovar}
                className="rounded-xl bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700"
              >
                Aprovar Documento
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Reprovação */}
      {showReprovarModal && documentoParaReprovar && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="w-full max-w-md rounded-3xl border border-slate-200 bg-white shadow-xl">
            <div className="border-b border-slate-200 p-6">
              <h3 className="text-lg font-semibold text-slate-900">
              Reprovar Documento
              </h3>
            </div>

            <div className="p-6">
            <div className="mb-4">
              <p>
                Você está prestes a reprovar o documento de {documentoParaReprovar.nomeOriginal}.
              </p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">
                  Motivo da Reprovação <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={motivoReprovacao}
                  onChange={(e) => setMotivoReprovacao(toUpperText(e.target.value))}
                  className="w-full border rounded px-3 py-2"
                  rows={3}
                  placeholder="Motivo da reprovação"
                />
              </div>
            </div>

            </div>
            <div className="flex justify-end space-x-2 border-t border-slate-200 bg-slate-50 p-6">
              <button
                onClick={() => setShowReprovarModal(false)}
                className="rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
              >
                Cancelar
              </button>
              <button
                onClick={handleReprovar}
                className="rounded-xl bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700"
              >
                Reprovar Documento
              </button>
            </div>
          </div>
        </div>
      )}

      {documentoEmVisualizacao && (
        <FileViewerModal
          url={documentoPessoalService.viewUrl(documentoEmVisualizacao.id)}
          nome={documentoEmVisualizacao.nomeOriginal}
          mimeType={documentoEmVisualizacao.tipoMime}
          subtitle="Documento pessoal do dirigente"
          onClose={() => setDocumentoEmVisualizacao(null)}
        />
      )}

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
};

export default DocumentosPessoaisUpload;
